import {
  DateFormat,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DISPLAY_MODE,
  DEFAULT_TIME_FORMAT,
  DEFAULT_TIME_STEP,
  DisplayMode,
  TimeFormat
} from '@treneo/shared/constants';
import { logger } from '@treneo/shared/logger';
import { getItemWithTtl } from '@treneo/shared/utils';

import {
  DEFAULT_BORDER_SIZE,
  DEFAULT_FONT_SIZE,
  DEFAULT_TAG_COLOR_MODE,
  DEFAULT_TAG_VARIANT,
  DEFAULT_UNIFORM_COLOR,
  TagColorMode,
  TagVariant
} from '../tags-config';

import type {
  DateTimePickerSettings,
  ListPaginationSettings,
  PaginationSettings,
  TagsSettings,
  TagStylingSettings
} from './types';

const MIN_PAGE_SIZE = 1;
const MAX_PAGE_SIZE = 10000;
const MIN_BORDER_SIZE = 1;
const MAX_BORDER_SIZE = 10;
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 24;

export const validateNumberInRange = (
  value: unknown,
  min: number,
  max: number,
  defaultValue: number
): number => {
  if (typeof value !== 'number' || !Number.isFinite(value) || isNaN(value)) {
    return defaultValue;
  }
  return Math.max(min, Math.min(Math.floor(value), max));
};

const validateBooleanValue = (value: unknown, defaultValue: boolean): boolean =>
  typeof value === 'boolean' ? value : defaultValue;

export const validateColorValue = (value: unknown, defaultValue: string): string => {
  if (typeof value === 'string') {
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      return value;
    }
    if (/^#[0-9A-Fa-f]{3}$/.test(value)) {
      const hex = value.slice(1);
      return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
    }
  }
  return defaultValue;
};

const validateTagStylingSettings = (value: unknown): TagStylingSettings => {
  const defaults = {
    uniformColor: DEFAULT_UNIFORM_COLOR,
    borderSize: DEFAULT_BORDER_SIZE,
    fontSize: DEFAULT_FONT_SIZE
  };
  if (!value || typeof value !== 'object') {
    return defaults;
  }
  const settings = value as Record<string, unknown>;
  return {
    uniformColor: validateColorValue(
      settings.uniformColor ?? defaults.uniformColor,
      defaults.uniformColor
    ),
    borderSize: validateNumberInRange(
      settings.borderSize,
      MIN_BORDER_SIZE,
      MAX_BORDER_SIZE,
      defaults.borderSize
    ),
    fontSize: validateNumberInRange(
      settings.fontSize,
      MIN_FONT_SIZE,
      MAX_FONT_SIZE,
      defaults.fontSize
    )
  };
};

const validateDateFormat = (value: unknown): DateFormat => {
  if (typeof value === 'string' && Object.values(DateFormat).includes(value as DateFormat)) {
    return value as DateFormat;
  }
  return DEFAULT_DATE_FORMAT;
};

const validateTimeFormat = (value: unknown): TimeFormat => {
  if (typeof value === 'string' && Object.values(TimeFormat).includes(value as TimeFormat)) {
    return value as TimeFormat;
  }
  return DEFAULT_TIME_FORMAT;
};

const validateDisplayMode = (value: unknown): DisplayMode => {
  if (typeof value === 'string' && Object.values(DisplayMode).includes(value as DisplayMode)) {
    return value as DisplayMode;
  }
  return DEFAULT_DISPLAY_MODE;
};

const validateDateTimePickerSettings = (value: unknown): DateTimePickerSettings => {
  const defaults = {
    dateFormat: DEFAULT_DATE_FORMAT,
    timeFormat: DEFAULT_TIME_FORMAT,
    displayMode: DEFAULT_DISPLAY_MODE,
    timeStep: DEFAULT_TIME_STEP,
    mondayFirst: true
  };
  if (!value || typeof value !== 'object') {
    return defaults;
  }
  const settings = value as Record<string, unknown>;
  return {
    dateFormat: validateDateFormat(settings.dateFormat ?? defaults.dateFormat),
    timeFormat: validateTimeFormat(settings.timeFormat),
    displayMode: validateDisplayMode(settings.displayMode),
    timeStep: validateNumberInRange(settings.timeStep, 60, 3600, defaults.timeStep),
    mondayFirst: validateBooleanValue(settings.mondayFirst, defaults.mondayFirst)
  };
};

const validateArrayOfNumbers = (
  value: unknown,
  defaultValue: number[],
  min: number = MIN_PAGE_SIZE,
  max: number = MAX_PAGE_SIZE
): number[] => {
  if (!Array.isArray(value)) {
    return defaultValue;
  }
  const validatedArray = value
    .filter(item => typeof item === 'number' && Number.isFinite(item) && !isNaN(item))
    .map(item => Math.max(min, Math.min(Math.floor(item), max)))
    .filter((item, index, arr) => arr.indexOf(item) === index)
    .sort((a, b) => a - b);
  return validatedArray.length > 0 ? validatedArray : (defaultValue ?? []);
};

const validateGlobalPaginationSettings = (value: unknown): PaginationSettings['global'] => {
  const defaults = {
    pageSizeOptions: [5, 10, 20, 50, 100],
    showFirstLastButtons: true,
    showPreviousNextButtons: true,
    showPageSizeSelector: true,
    showPageInfo: true
  };
  if (!value || typeof value !== 'object') {
    return defaults;
  }
  const settings = value as Record<string, unknown>;
  return {
    pageSizeOptions: validateArrayOfNumbers(
      settings.pageSizeOptions,
      defaults.pageSizeOptions,
      MIN_PAGE_SIZE,
      MAX_PAGE_SIZE
    ),
    showFirstLastButtons: validateBooleanValue(
      settings.showFirstLastButtons,
      defaults.showFirstLastButtons
    ),
    showPreviousNextButtons: validateBooleanValue(
      settings.showPreviousNextButtons,
      defaults.showPreviousNextButtons
    ),
    showPageSizeSelector: validateBooleanValue(
      settings.showPageSizeSelector,
      defaults.showPageSizeSelector
    ),
    showPageInfo: validateBooleanValue(settings.showPageInfo, defaults.showPageInfo)
  };
};

const validateListPaginationSettings = (
  value: unknown,
  defaultSettings: ListPaginationSettings
): ListPaginationSettings => {
  if (!value || typeof value !== 'object') {
    return defaultSettings;
  }
  const settings = value as Record<string, unknown>;
  return {
    pageSize: validateNumberInRange(
      settings.pageSize,
      MIN_PAGE_SIZE,
      MAX_PAGE_SIZE,
      defaultSettings.pageSize
    ),
    maxPageSize: validateNumberInRange(
      settings.maxPageSize,
      MIN_PAGE_SIZE,
      MAX_PAGE_SIZE,
      defaultSettings.maxPageSize
    )
  };
};

export const createDefaultListPaginationSettings = (
  pageSize: number = 20
): ListPaginationSettings => ({
  pageSize: validateNumberInRange(pageSize, MIN_PAGE_SIZE, MAX_PAGE_SIZE, 20),
  maxPageSize: 100
});

export const createDefaultPaginationSettings = (): PaginationSettings => ({
  global: {
    pageSizeOptions: [5, 10, 20, 50, 100],
    showFirstLastButtons: true,
    showPreviousNextButtons: true,
    showPageSizeSelector: true,
    showPageInfo: true
  },
  lists: {
    courses: {
      pageSize: 20,
      maxPageSize: 100
    }
  }
});

export const loadPaginationSettingsFromStorage = (): PaginationSettings => {
  try {
    const stored = getItemWithTtl<PaginationSettings>('paginationSettings');
    if (!stored) {
      return createDefaultPaginationSettings();
    }
    if (typeof stored !== 'object') {
      return createDefaultPaginationSettings();
    }
    const defaults = createDefaultPaginationSettings();
    return {
      global: validateGlobalPaginationSettings(stored.global),
      lists: {
        courses: validateListPaginationSettings(stored.lists?.courses, defaults.lists.courses)
      }
    };
  } catch (error) {
    logger.warn('Failed to load pagination settings from localStorage:', error);
    return createDefaultPaginationSettings();
  }
};

export const loadTagsSettingsFromStorage = (): TagsSettings => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return {
        variant: DEFAULT_TAG_VARIANT,
        colorMode: DEFAULT_TAG_COLOR_MODE,
        styling: {
          uniformColor: DEFAULT_UNIFORM_COLOR,
          borderSize: DEFAULT_BORDER_SIZE,
          fontSize: DEFAULT_FONT_SIZE
        }
      };
    }
    const stored = getItemWithTtl<TagsSettings>('tagsSettings');
    if (stored) {
      return {
        variant: Object.values(TagVariant).includes(stored.variant)
          ? stored.variant
          : DEFAULT_TAG_VARIANT,
        colorMode: Object.values(TagColorMode).includes(stored.colorMode)
          ? stored.colorMode
          : DEFAULT_TAG_COLOR_MODE,
        styling: validateTagStylingSettings(stored.styling)
      };
    }
  } catch (error) {
    logger.warn('Failed to load tags settings from localStorage:', error);
  }
  return {
    variant: DEFAULT_TAG_VARIANT,
    colorMode: DEFAULT_TAG_COLOR_MODE,
    styling: {
      uniformColor: DEFAULT_UNIFORM_COLOR,
      borderSize: DEFAULT_BORDER_SIZE,
      fontSize: DEFAULT_FONT_SIZE
    }
  };
};

export const loadDateTimePickerSettingsFromStorage = (): DateTimePickerSettings => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return {
        dateFormat: DEFAULT_DATE_FORMAT,
        timeFormat: DEFAULT_TIME_FORMAT,
        displayMode: DEFAULT_DISPLAY_MODE,
        timeStep: DEFAULT_TIME_STEP,
        mondayFirst: true
      };
    }
    const stored = getItemWithTtl<DateTimePickerSettings>('dateTimePickerSettings');
    if (stored) {
      return validateDateTimePickerSettings(stored);
    }
  } catch (error) {
    logger.warn('Failed to load DateTimePicker settings from localStorage:', error);
  }
  return {
    dateFormat: DEFAULT_DATE_FORMAT,
    timeFormat: DEFAULT_TIME_FORMAT,
    displayMode: DEFAULT_DISPLAY_MODE,
    timeStep: DEFAULT_TIME_STEP,
    mondayFirst: true
  };
};

export const getDefaultConfigUiState = () => ({
  paginationSettings: loadPaginationSettingsFromStorage(),
  tagsSettings: loadTagsSettingsFromStorage(),
  dateTimePickerSettings: loadDateTimePickerSettingsFromStorage()
});
